<script lang="ts">
	import type { Page } from "@sveltejs/kit";

	import { Vertical } from '$lib/def/verticals';
	import { logout, profile$ } from "$lib/serv/core/auth";
	import { observe, setItem } from '$lib/serv/core/local-store';
	import { cart$, cartItemQuantity$ } from "$lib/serv/food-cart";
	import { DEFAULT_MERCHANT_KEY, cartItemQuantityGroc$, getLocalCartStream } from '$lib/serv/groc-cart';
	import { streamSelectedStore } from "$lib/serv/groc-landing";
	import { LOCATION_PROPS, browsingLocation$, geoOpted$, getRange, setGeoOpted, streamOptimisticLocation } from "$lib/serv/locations";
	import { closeAllModals } from "$lib/serv/modal";
	import { addressModalIsOpen$, closeAddressModal, selectAddress } from '$lib/ui/modals/AddressModal.svelte';
	import { openFeedbackForm } from "$lib/ui/profile/InfoDump.svelte";
	import { getAddressLabel, xaddress } from '$lib/util/address';
	import { formatCurrency } from "$lib/util/format";
	import { longpress } from "$lib/util/ui";
	import { query } from "$lib/util/url";
	import { BehaviorSubject, map } from "rxjs";
	import { fade } from "svelte/transition";
	import { isLocationAwareRoute, showSignInModal } from '$lib/serv/nav';
  
	import SignIn from '$lib/ui/profile/SignIn.svelte';
	import GlobalLoader from "$lib/ui/shared/GlobalLoader.svelte";
	import Modal from '$lib/ui/shared/Modal.svelte';
	import NavLoader from '$lib/ui/shared/NavLoader.svelte';
	import ToolTip, { ToolPosition } from '../shared/ToolTip.svelte';
	import Drawer from "./Drawer.svelte";
	import Logo from "../shared/icons/Logo.svelte";

	export let page: Page;
	const subjPage = new BehaviorSubject<Page>(page);
	$: subjPage.next(page);

	let selectedStore$ = streamSelectedStore(subjPage.pipe(map(p => p.url)));
	// let showCart = false;
	let navExclusionList: string[] = ['SnackMe Vouchers'];
	let showLogin = $showSignInModal;
	let showSignOutModal = false;

	const signOut = () => {
    logout();
    showSignOutModal = false;
  }

	// let expanded = getItem('showContextMenu') ?? false;
	let drawer = false;

	// let scrollY: number;
	// let scrollYPrev: number;
	// let innerWidth: number;
	// let scrolled = false;

	// function onScroll(y: number, width: number) {
	// 	if (width > 1024) return;
	// 	if (scrollYPrev < y - 5 && !scrolled && y > 300) {
	// 		scrolled = true;
	// 	} else if (scrollYPrev > y + 5 && scrolled) {
	// 		scrolled = false;
	// 	}
	// 	scrollYPrev = y;
	// }
		
	// $: onScroll(scrollY, innerWidth);

	// let toggleNav = () => setItem('showContextMenu', expanded = !expanded);

	// const foodCart$ = getActiveCart();

	// Grocery Cart
	const grocCart$ = getLocalCartStream(DEFAULT_MERCHANT_KEY);

	$: vertical = page.url.searchParams.get('vertical') ?? Vertical.Restaurant;
	$: isLoggedIn = profile$.pipe(map((user) => !!user));

	const location$ = streamOptimisticLocation(subjPage.pipe(map(p => p.route.id)), profile$)

	let inRange$ = getRange();

	const showCart$ = observe<boolean>('showCart').pipe(
		map((store) => store.value ?? false),
	);

	function updateShowCart(show: boolean) {
		if (show) closeAllModals(page.url);
		setItem('showCart', show);
	}

	async function currentLocation() {
		setGeoOpted();
		openSelectAddress(); // TODO: depending on which page you are, select address might not need to go home
	}

	const toggleAddressModal = (addressModalIsOpen: boolean) => {
		if (addressModalIsOpen) closeAddressModal();
		else {
			closeAllModals(page.url);
			openSelectAddress();
		}
	}

	const openSelectAddress = () => {
		const stay = isLocationAwareRoute(page.route.id ?? '/');
		let params:['vertical', Vertical] | undefined;
		if (!stay) {
			switch (page.route.id) {
				case '/(app)/delivery/shops':
					params = ['vertical', Vertical.Store];
					break;
				case '/(app)/delivery/groceries':
					params = ['vertical', Vertical.Grocery];
					break;
				// case '/(app)/delivery/restaurants': // we don't need to set this param
				// 	params = ['vertical', Vertical.Restaurant];
				// 	break;
			}
		}
		selectAddress(stay, params && [params]);
	}
</script>

<svelte:body class="has-navbar" />

{#if drawer}
	<div on:click={() => drawer = false} on:keypress={(e) => e.key === 'Enter' && (drawer = false)} class="drawer" transition:fade={{ duration: 200 }} role="button" tabindex="0">
		<Drawer {page} auth={(signin) => {
			if (signin) showSignInModal.update(() => true);
			else showSignOutModal = true;
		}} />
	</div>
{/if}

<div class="navbar" data-testid="nav-bar">
	<GlobalLoader />
	<div class="main-top">
		<div>
			<a 
				href={'/?' + query(page.url).setBulk($location$).keep(...LOCATION_PROPS)} 
				class="logo" 
				use:longpress={1000} 
				on:longpress={() => openFeedbackForm(page.url.toString())}
				on:click={() => closeAllModals(page.url)}>
				<Logo />
			</a>
			<button class="menu-icon" on:click={() => {drawer = !drawer; closeAllModals(page.url)}} aria-label="menu">
				<span class="icon-menu-28" style="font-size: 15px; transform: unset;"></span>
			</button>
	
			{#if $browsingLocation$?.hasGeo}
				<article class="nav-address-search" class:hideSearch={!$inRange$}>
					<div on:click={() => toggleAddressModal($addressModalIsOpen$)} on:keypress={(e) => e.key === 'Enter' && toggleAddressModal($addressModalIsOpen$)} class="box address" class:mini={vertical === Vertical.Grocery} role="button" tabindex="0" data-testid="btn-change-addr">
						<span class="icon-pin-outline-20"></span>
						<article>
						{#if $location$}
							{@const store = $selectedStore$}
							{@const homeParams = $location$}
							{#if !homeParams.hasGeo}
								<div class="body-m">No location</div>
							{:else if homeParams.label}
								<div class="body-m ellipses">{getAddressLabel(homeParams.label)}</div>
							{:else}
								<div class="body-m ellipses">{xaddress(homeParams).remove("province", "postal_code").toStr()}</div>
							{/if}
						{/if}
						</article>
					</div>
				</article>
			{:else}
				<article class="signed-out-top-nav">
					<section class="flex justify-start medium-gap">
						<button class="button m-0" on:click={() => openSelectAddress()} data-testid="btn-select-addr"><span class="icon-pin-outline-20"></span>Enter address</button>
						<!-- TODO: find out if this can be changed - this feature is available when you click the enter address button -->
						{#if !$geoOpted$}
							<button class="label-xl m-0 medium" on:click={(e) => currentLocation()} data-testid="btn-current-addr"><span class="icon-current-location-20"></span>Find my location</button>
						{/if}
					</section>
					{#if !$isLoggedIn}
						<button class="button tertiary label-xl medium m-0" on:click={() => showSignInModal.update(() => true)} data-testid="btn-signin">Sign in for saved addresses</button>
					{/if}
				</article>
			{/if}
		</div>
	
		<article class="nav-cart-login">

			<!-- CART BUTTON: FOOD && SHOPS -->
			{#if $browsingLocation$?.hasGeo && vertical !== Vertical.Grocery}
				{@const price = $cart$?.totals?.subtotal ?? 0}
				{@const cartHasItems = $cart$?.items && $cart$?.items.length > 0 ? true: false}
				<button class="cart-action"
					class:button-disabled={!$cart$ || !cartHasItems}
					on:click={() => updateShowCart(!$showCart$)}
					data-testid="show-cart">
					<span class="cart-icon icon-cart-28"></span>
					<span class="mobile-count" data-testid="mobile-num-items">{$cartItemQuantity$ ? $cartItemQuantity$ : 0}</span>
					<div class="cart-text">
						<span data-testid="cart-total">{formatCurrency(price)}</span>
						<span class="cartItems nowrap" data-testid="num-items">{$cartItemQuantity$ ? $cartItemQuantity$ : 0 } 
							Item{$cartItemQuantity$ === 1 ? '' : 's'}</span>
					</div>
					{#if price <= 0}
						<ToolTip message="Your cart is empty" pos={ToolPosition.Left} />
					{/if}
				</button>
			{/if}

			<!-- CART BUTTON: GROC -->
			{#if $browsingLocation$?.hasGeo && vertical === Vertical.Grocery}
				{@const grocCart = $grocCart$}
				{@const grocItemCount = $cartItemQuantityGroc$ ?? 0}
				{@const cartTotal = grocCart?.totals?.subtotal ?? 0}
				{@const cartHasItems = grocCart?.items && grocCart?.items.length > 0 ? true: false}
				<button class="cart-action"
					class:button-disabled={!grocCart || !cartHasItems}
					on:click={() => updateShowCart(!$showCart$)}
					data-testid="show-cart">
					<span class="cart-icon icon-cart-28"></span>
					<span class="mobile-count" data-testid="mobile-num-items">{grocItemCount || 0}</span>
					<div class="cart-text">
						<span data-testid="cart-total">{formatCurrency(cartTotal)}</span>
						<span class="cartItems nowrap" data-testid="num-items">{grocItemCount} 
							Item{grocItemCount === 1 ? '': 's'}</span>
					</div>
					{#if !grocCart || !cartHasItems}
						<ToolTip message="Your cart is empty" pos={ToolPosition.Left} />
					{/if}
				</button>
			{/if}

			<NavLoader/>

		</article>
	</div>
	
</div>

{#if $showSignInModal}
	<Modal on:close={() => showSignInModal.update(() => false)} id="signin">
		<SignIn on:ok={() => showSignInModal.update(() => false)}/>
	</Modal>
{/if}

{#if showSignOutModal }
    <Modal on:close={() => showSignOutModal = false} id="signout">
      <span  class="headline-m bold">Are you sure you want to sign out?</span>
      <div slot="footer">
        <button class="button secondary" style="margin-right: 1rem;" on:click={() => showSignOutModal = false}>cancel</button>
        <button class="button primary" on:click={() => signOut()}>Sign out</button>
      </div>
    </Modal>
  {/if}

<style lang="scss">
	@use 'padding';
	@use 'layout';

	// .scrolled .mobile-verticals { bottom: -68px; }
	.main-below.scrolled { transform: translateY(-100%); }
	// .main-below { button.has-location-button { padding: 1.5px; } }
</style>

